import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomerService } from '../../../Services/customer.service';
import { EquipmentService } from '../../../Services/equipment.service';
import { MaintenanceService } from '../../../Services/maintenance.service';
import { UserService } from '../../../Services/user.service';
import { StammkundeService } from '../../../Services/stammkunde.service';
import {
  Customer,
  CustomerHourlyRate,
  CustomerHourlyRatePerson,
  CustomerMaterialItem
} from '../../../Models/customer.model';
import {
  MyStammkundeMembership,
  TakedaLeistungsverzeichnis,
  TakedaLvPosition,
  TakedaLvPositionSave
} from '../../../Models/stammkunde.model';
import { Equipment } from '../../../Models/equipment.model';
import { Maintenance, MaintenanceStatus, MaintenanceRecurrenceType, User } from '../../../Models/maintenance.model';
import { CustomerFormComponent } from '../customer-form/customer-form.component';
import { EquipmentFormComponent } from '../equipment-form/equipment-form.component';
import { MaintenanceFormComponent } from '../maintenance-form/maintenance-form.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { AuthService } from '../../../api-authorization/authorize.service';
import { EinsatzAnsichtStateService } from '../../../Services/einsatz-ansicht-state.service';

interface ServiceCallPersonOption {
  key: string;
  label: string;
  count: number;
}

/** Was nach dem Laden wiederhergestellt werden soll. */
/**
 * Eindeutiger Schlüssel einer Person in der Stundensatz-Zuordnung.
 *
 * Bewusst außerhalb der Komponente: trackBy-Funktionen werden von Angular ohne
 * Komponentenkontext aufgerufen, dort ist `this` nicht die Komponente.
 */
function buildHourlyRatePersonKey(person: CustomerHourlyRatePerson): string {
  return `${person.personType}:${person.userId ?? person.workerId}`;
}

interface PendingScrollTarget {
  scrollY: number;
  ankerId: number | null;
  ankerOffset: number;
}

@Component({
  selector: 'app-customer-detail',
  templateUrl: './customer-detail.component.html',
  styleUrls: ['./customer-detail.component.css']
})
export class CustomerDetailComponent implements OnInit {
  customer: Customer | null = null;
  loading = true;
  activeTab = 0;
  users: User[] = [];
  private readonly tabStoragePrefix = 'wartung-customer-tab-';
  private readonly maxTabIndex = 3;
  private touchStartX: number | null = null;
  private touchStartY: number | null = null;
  private allMaintenancesCache: Maintenance[] = [];
  private plannedMaintenancesCache: Maintenance[] = [];
  private serviceCallMaintenancesCache: Maintenance[] = [];
  private filteredServiceCallMaintenancesCache: Maintenance[] = [];
  private maintenancesWithMaterialsCache: Maintenance[] = [];
  serviceCallPersonFilter = 'all';
  serviceCallPersonOptions: ServiceCallPersonOption[] = [];

  /** Index des Tabs „Einsätze & Reparaturen". */
  private readonly serviceCallTabIndex = 2;

  /** Ziel, das nach dem Laden angesteuert wird. `null` = nichts zu tun. */
  private pendingScroll: PendingScrollTarget | null = null;

  private maintenanceCountByEquipmentId = new Map<number, number>();
  private lastMaintenanceDateByEquipmentId = new Map<number, Date>();
  private completionPercentCache = 0;
  private openMaintenanceCountCache = 0;
  private overdueMaintenanceCountCache = 0;

  // ── Takeda-Leistungsverzeichnis (nur für den konfigurierten Takeda-Kunden) ──
  takedaLeistungsverzeichnisse: TakedaLeistungsverzeichnis[] = [];
  takedaLvPositionen: TakedaLvPosition[] = [];
  takedaLvLoading = false;
  takedaLvImporting = false;
  takedaLvPositionsLoading = false;
  takedaLvPositionsVisible = false;
  takedaLvSearch = '';
  takedaLvPageSize = 15;
  takedaLvCurrentPage = 1;
  selectedTakedaLvFile: File | null = null;
  takedaLvAdding = false;
  takedaLvSaving = false;
  editingTakedaLvPositionId: number | null = null;
  takedaLvNewPosition: TakedaLvPositionSave = this.createEmptyTakedaLvPosition();
  takedaLvEditPosition: TakedaLvPositionSave | null = null;
  private myMemberships: MyStammkundeMembership[] = [];

  // ── Stundensatz-Zuordnung (nur Takeda) ──
  hourlyRatePersons: CustomerHourlyRatePerson[] = [];
  hourlyRatePersonsLoading = false;
  /** Zeile, die gerade gespeichert wird – blockiert genau dieses Select. */
  savingAssignmentKey: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private equipmentService: EquipmentService,
    private maintenanceService: MaintenanceService,
    private userService: UserService,
    private stammkundeService: StammkundeService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private einsatzAnsichtState: EinsatzAnsichtStateService,
    private host: ElementRef<HTMLElement>
  ) {}

  /** Montage = nur lesen: Anlegen/Bearbeiten-Aktionen ausblenden. */
  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      const tab = this.parseTabIndex(params.get('tab'));
      if (tab !== null) {
        this.activeTab = tab;
        this.persistActiveTab();
      }
    });

    this.route.params.subscribe(params => {
      const id = +params['id'];
      // Nur beim Betreten der Seite an die gemerkte Stelle springen – nach einem
      // Neuladen aus einem Dialog heraus soll die Ansicht stehen bleiben.
      if (id) this.loadCustomer(id, true);
    });
    this.userService.getUsers().subscribe(u => this.users = u ?? []);
  }

  loadCustomer(id: number, restoreScroll = false): void {
    this.loading = true;

    // Gemerkten Filter VOR dem Aufbau der Zwischenspeicher setzen: der Aufbau
    // prüft ihn gegen die tatsächlich vorhandenen Personen und fällt auf „alle"
    // zurück, wenn die Person keinen Einsatz mehr hat.
    const gemerkt = this.einsatzAnsichtState.holen(id);
    const gemerkterFilter = gemerkt?.personFilter ?? 'all';
    this.serviceCallPersonFilter = gemerkterFilter;
    this.pendingScroll = restoreScroll && gemerkt
      ? { scrollY: gemerkt.scrollY, ankerId: gemerkt.ankerId, ankerOffset: gemerkt.ankerOffset }
      : null;

    this.customerService.getCustomer(id).subscribe({
      next: (c) => {
        this.customer = c;
        this.rebuildMaintenanceCaches();
        const routeTab = this.parseTabIndex(this.route.snapshot.queryParamMap.get('tab'));
        this.activeTab = routeTab ?? this.getStoredTab(c.id) ?? this.activeTab;
        this.loading = false;

        // Hat der Aufbau den Filter verworfen (Person hat keinen Einsatz mehr),
        // passt auch die gemerkte Scrollposition nicht mehr zur Liste.
        if (this.serviceCallPersonFilter !== gemerkterFilter) {
          this.pendingScroll = null;
          this.persistEinsatzAnsichtOben();
        }

        this.restoreScrollPosition();

        this.handlePendingActionFromQuery();

        // Takeda-Sonderfunktionen nur für den konfigurierten Takeda-Kunden laden.
        if (this.isTakedaCustomer) {
          this.loadTakedaLvBerechtigung();
          this.loadTakedaLeistungsverzeichnisse();
          this.loadHourlyRateAssignments();
        } else {
          this.resetTakedaLvState();
          this.hourlyRatePersons = [];
        }
      },
      error: () => {
        this.snackBar.open('Kunde nicht gefunden', 'OK', { duration: 4000 });
        this.router.navigate(['/wartung/kunden']);
      }
    });
  }

  reload(): void {
    if (this.customer) this.loadCustomer(this.customer.id);
  }

  /**
   * Wird die Seite mit ?action=plan|service geöffnet (z.B. aus den Schnellaktionen
   * im Dashboard), startet hier direkt der passende Dialog. Der Query-Parameter
   * wird danach entfernt, damit er beim Neuladen nicht erneut auslöst.
   */
  private handlePendingActionFromQuery(): void {
    const action = this.route.snapshot.queryParamMap.get('action');
    if (!action) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { action: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });

    setTimeout(() => {
      if (action === 'plan') {
        this.selectTab(1);
        this.addPlannedMaintenance();
      } else if (action === 'service') {
        this.selectTab(2);
        this.addServiceCall();
      }
    }, 0);
  }

  editCustomer(): void {
    if (!this.customer) return;
    const dialogRef = this.dialog.open(CustomerFormComponent, {
      width: '880px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      data: { ...this.customer }
    });
    dialogRef.afterClosed().subscribe(r => { if (r) this.reload(); });
  }

  // --- Equipment / Verteiler ---
  get equipments(): Equipment[] {
    return this.customer?.equipments ?? [];
  }

  addEquipment(): void {
    if (!this.customer) return;
    const dialogRef = this.dialog.open(EquipmentFormComponent, {
      width: '480px',
      data: { customerId: this.customer.id }
    });
    dialogRef.afterClosed().subscribe(r => { if (r) this.reload(); });
  }

  editEquipment(eq: Equipment, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(EquipmentFormComponent, {
      width: '480px',
      data: { ...eq, customerId: this.customer!.id }
    });
    dialogRef.afterClosed().subscribe(r => { if (r) this.reload(); });
  }

  deleteEquipment(eq: Equipment, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Verteiler löschen', message: `"${eq.name}" wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(r => {
      if (r) {
        this.equipmentService.deleteEquipment(eq.id).subscribe({
          next: () => { this.reload(); this.snackBar.open('Gelöscht', 'OK', { duration: 2000 }); },
          error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  getMaintenanceCountForEquipment(eq: Equipment): number {
    return this.maintenanceCountByEquipmentId.get(eq.id) ?? 0;
  }

  getLastMaintenanceDate(eq: Equipment): Date | null {
    return this.lastMaintenanceDateByEquipmentId.get(eq.id) ?? null;
  }

  // --- Maintenances ---
  get allMaintenances(): Maintenance[] {
    return this.allMaintenancesCache;
  }

  get plannedMaintenances(): Maintenance[] {
    return this.plannedMaintenancesCache;
  }

  get serviceCallMaintenances(): Maintenance[] {
    return this.serviceCallMaintenancesCache;
  }

  get filteredServiceCallMaintenances(): Maintenance[] {
    return this.filteredServiceCallMaintenancesCache;
  }

  onServiceCallPersonFilterChange(): void {
    this.applyServiceCallPersonFilter();
    // Nach dem Umschalten steht die Liste wieder oben – die alte Position
    // gehörte zu einer anderen Auswahl.
    this.persistEinsatzAnsichtOben();
  }

  resetServiceCallPersonFilter(): void {
    this.serviceCallPersonFilter = 'all';
    this.applyServiceCallPersonFilter();
    this.persistEinsatzAnsichtOben();
  }

  get maintenancesWithMaterials(): Maintenance[] {
    return this.maintenancesWithMaterialsCache;
  }

  addPlannedMaintenance(): void {
    if (!this.customer || this.equipments.length === 0) {
      this.snackBar.open('Bitte zuerst einen Verteiler/Anlage anlegen', 'OK', { duration: 3000 });
      return;
    }
    const dialogRef = this.dialog.open(MaintenanceFormComponent, {
      width: '640px',
      data: { customerId: this.customer.id, customerName: this.customer.name, equipments: this.equipments, users: this.users, type: 'planned' }
    });
    dialogRef.afterClosed().subscribe(r => { if (r) this.reload(); });
  }

  addServiceCall(): void {
    if (!this.customer) return;
    if (this.equipments.length === 0) {
      this.snackBar.open('Bitte zuerst einen Verteiler/Anlage anlegen', 'OK', { duration: 3000 });
      return;
    }
    // Navigate to the dedicated service call form
    this.persistActiveTab();
    this.persistEinsatzAnsicht();
    this.router.navigate(['/wartung/einsatz-neu', this.customer.id]);
  }

  openMaintenance(m: Maintenance): void {
    this.persistActiveTab();
    // Filter und Scrollposition festhalten, damit die Rückkehr aus dem Einsatz
    // genau hier weitergeht.
    this.persistEinsatzAnsicht();
    this.router.navigate(['/wartung/auftrag', m.id], {
      queryParams: { customerTab: this.activeTab }
    });
  }

  selectTab(index: number): void {
    if (index === this.activeTab) return;

    this.activeTab = index;
    this.persistActiveTab();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: index },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  onTabChanged(index: number): void {
    this.selectTab(index);
  }

  onTabTouchStart(event: TouchEvent): void {
    const touch = event.changedTouches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  onTabTouchEnd(event: TouchEvent): void {
    if (this.touchStartX === null || this.touchStartY === null) return;

    const touch = event.changedTouches[0];
    const diffX = touch.clientX - this.touchStartX;
    const diffY = touch.clientY - this.touchStartY;
    this.touchStartX = null;
    this.touchStartY = null;

    const isHorizontalSwipe = Math.abs(diffX) > 55 && Math.abs(diffX) > Math.abs(diffY) * 1.4;
    if (!isHorizontalSwipe) return;

    const nextTab = diffX < 0
      ? Math.min(this.maxTabIndex, this.activeTab + 1)
      : Math.max(0, this.activeTab - 1);

    if (nextTab !== this.activeTab) {
      this.selectTab(nextTab);
    }
  }

  onTabTouchCancel(): void {
    this.touchStartX = null;
    this.touchStartY = null;
  }

  /**
   * Hält Personenfilter und Position der Einsatzliste fest – aufgerufen, bevor
   * ein Einsatz geöffnet wird.
   *
   * Gemerkt wird beides: der oberste sichtbare Einsatz als Anker und die rohe
   * Scrollposition als Rückfallebene. Der Anker trifft auch dann, wenn die
   * Liste beim Zurückkommen anders hoch ausfällt.
   */
  private persistEinsatzAnsicht(): void {
    if (!this.customer) return;

    const bereich = this.scrollBereich();
    const anker = this.ermittleAnker(bereich);

    this.einsatzAnsichtState.merken(this.customer.id, {
      personFilter: this.serviceCallPersonFilter,
      scrollY: this.scrollPosition(bereich),
      ankerId: anker?.id ?? null,
      ankerOffset: anker?.offset ?? 0
    });
  }

  /** Merkt den Filter, aber ohne Position – die Liste startet wieder oben. */
  private persistEinsatzAnsichtOben(): void {
    if (!this.customer) return;

    this.einsatzAnsichtState.merken(this.customer.id, {
      personFilter: this.serviceCallPersonFilter,
      scrollY: 0,
      ankerId: null,
      ankerOffset: 0
    });
  }

  /**
   * Der tatsächlich scrollende Vorfahr der Einsatzliste.
   *
   * Wichtig: die Seite scrollt NICHT im Fenster. Der Router-Inhalt steckt in
   * einem <c>mat-sidenav-content</c>, und das ist das Element mit der
   * Bildlaufleiste. Statt das fest zu verdrahten, wird von der Liste aus nach
   * oben gesucht – so bleibt es auch dann richtig, wenn sich das Layout ändert.
   *
   * <c>null</c> heisst: es scrollt das Fenster.
   */
  private scrollBereich(): HTMLElement | null {
    let node: HTMLElement | null = this.host.nativeElement;
    let ersterKandidat: HTMLElement | null = null;

    while (node && node !== document.body && node !== document.documentElement) {
      const overflowY = getComputedStyle(node).overflowY;

      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        // Bevorzugt der Bereich, der gerade wirklich scrollt. Direkt nach dem
        // Laden ist die Liste noch kurz – dann dient der erste Kandidat.
        if (node.scrollHeight > node.clientHeight + 1) return node;
        ersterKandidat ??= node;
      }

      node = node.parentElement;
    }

    return ersterKandidat;
  }

  private scrollPosition(bereich: HTMLElement | null): number {
    return bereich
      ? bereich.scrollTop
      : window.scrollY || document.documentElement.scrollTop || 0;
  }

  private setzeScrollPosition(bereich: HTMLElement | null, wert: number): void {
    if (bereich) {
      bereich.scrollTop = wert;
    } else {
      window.scrollTo({ top: wert, behavior: 'auto' });
    }
  }

  private maxScrollPosition(bereich: HTMLElement | null): number {
    return bereich
      ? Math.max(0, bereich.scrollHeight - bereich.clientHeight)
      : Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  }

  /** Oberkante des Scrollbereichs im Fensterkoordinatensystem. */
  private bereichOberkante(bereich: HTMLElement | null): number {
    return bereich ? bereich.getBoundingClientRect().top : 0;
  }

  private einsatzKarte(id: number): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>(
      `[data-service-call-id="${id}"]`
    );
  }

  /**
   * Oberster Einsatz, der noch (teilweise) sichtbar ist, samt seinem Abstand
   * zur Oberkante des Scrollbereichs.
   */
  private ermittleAnker(bereich: HTMLElement | null): { id: number; offset: number } | null {
    const karten = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('[data-service-call-id]')
    );
    if (karten.length === 0) return null;

    const oberkante = this.bereichOberkante(bereich);

    for (const karte of karten) {
      const rect = karte.getBoundingClientRect();
      if (rect.bottom <= oberkante + 1) continue;

      const id = Number(karte.getAttribute('data-service-call-id'));
      if (!Number.isFinite(id)) return null;

      return { id, offset: Math.round(rect.top - oberkante) };
    }

    return null;
  }

  /**
   * Springt nach dem Laden zurück an die gemerkte Stelle.
   *
   * Beim Laden ist die Liste noch nicht da – sie rendert erst, und der
   * Tabwechsel läuft animiert. Deshalb wird über mehrere Frames hinweg gewartet,
   * bis der Anker existiert bzw. der Bereich hoch genug ist; ein einzelner
   * Versuch würde ins Leere laufen.
   */
  private restoreScrollPosition(): void {
    const ziel = this.pendingScroll;
    this.pendingScroll = null;

    // Nur im Tab der Einsatzliste – auf den anderen Tabs wäre ein Sprung
    // mitten in die Seite unerwartet.
    if (!ziel || this.activeTab !== this.serviceCallTabIndex) return;
    if (ziel.ankerId == null && ziel.scrollY <= 0) return;

    // Reicht für Rendern plus die 200 ms Tab-Animation.
    let verbleibendeVersuche = 60;

    const versuchen = () => {
      const bereich = this.scrollBereich();
      const anker = ziel.ankerId != null ? this.einsatzKarte(ziel.ankerId) : null;

      if (anker) {
        const abstand =
          anker.getBoundingClientRect().top - this.bereichOberkante(bereich);
        const delta = abstand - ziel.ankerOffset;

        // Sitzt bereits richtig – fertig.
        if (Math.abs(delta) < 1) return;

        const max = this.maxScrollPosition(bereich);
        const neu = Math.min(max, Math.max(0, this.scrollPosition(bereich) + delta));
        this.setzeScrollPosition(bereich, neu);

        // Noch ein Frame: nachrückende Inhalte können die Höhen verschieben.
        if (--verbleibendeVersuche > 0) requestAnimationFrame(versuchen);
        return;
      }

      // Anker noch nicht gerendert: warten, statt vorschnell auf die rohe
      // Position zu springen.
      if (ziel.ankerId != null && verbleibendeVersuche > 0) {
        verbleibendeVersuche--;
        requestAnimationFrame(versuchen);
        return;
      }

      const max = this.maxScrollPosition(bereich);
      if (max >= ziel.scrollY || verbleibendeVersuche <= 0) {
        this.setzeScrollPosition(bereich, Math.min(ziel.scrollY, max));
        return;
      }

      verbleibendeVersuche--;
      requestAnimationFrame(versuchen);
    };

    requestAnimationFrame(versuchen);
  }

  private persistActiveTab(): void {
    if (!this.customer) return;
    try {
      sessionStorage.setItem(`${this.tabStoragePrefix}${this.customer.id}`, String(this.activeTab));
    } catch { /* ignore */ }
  }

  private getStoredTab(customerId: number): number | null {
    try {
      return this.parseTabIndex(sessionStorage.getItem(`${this.tabStoragePrefix}${customerId}`));
    } catch {
      return null;
    }
  }

  private parseTabIndex(value: string | null): number | null {
    if (value == null || value.trim() === '') return null;
    const tab = Number(value);
    return Number.isInteger(tab) && tab >= 0 && tab <= 3 ? tab : null;
  }

  private rebuildMaintenanceCaches(): void {
    const equipments = this.customer?.equipments ?? [];
    const all = equipments.flatMap(equipment =>
      (equipment.maintenances ?? []).map(maintenance => ({ ...maintenance, equipment }))
    );

    this.allMaintenancesCache = all;
    this.plannedMaintenancesCache = all
      .filter(m => m.recurrenceType !== MaintenanceRecurrenceType.None)
      .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime());
    this.serviceCallMaintenancesCache = all
      .filter(m => m.recurrenceType === MaintenanceRecurrenceType.None)
      .sort((a, b) => new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime());
    this.rebuildServiceCallPersonOptions();
    this.applyServiceCallPersonFilter();
    this.maintenancesWithMaterialsCache = all.filter(m => (m.materials ?? []).length > 0);

    this.maintenanceCountByEquipmentId = new Map(
      equipments.map(equipment => [equipment.id, equipment.maintenances?.length ?? 0])
    );
    this.lastMaintenanceDateByEquipmentId = new Map();
    for (const equipment of equipments) {
      const lastCompleted = (equipment.maintenances ?? [])
        .filter(m => m.status === MaintenanceStatus.Completed && m.completedOn)
        .map(m => new Date(m.completedOn!))
        .sort((a, b) => b.getTime() - a.getTime())[0];
      if (lastCompleted) {
        this.lastMaintenanceDateByEquipmentId.set(equipment.id, lastCompleted);
      }
    }

    const done = all.filter(m => m.status === MaintenanceStatus.Completed).length;
    this.completionPercentCache = all.length === 0 ? 0 : Math.round((done / all.length) * 100);
    this.openMaintenanceCountCache = all.filter(m =>
      m.status !== MaintenanceStatus.Completed &&
      m.status !== MaintenanceStatus.Cancelled
    ).length;
    this.overdueMaintenanceCountCache = all.filter(m => m.status === MaintenanceStatus.Overdue).length;
  }

  /**
   * Alle Personen (Techniker/Mitarbeiter), die einem Einsatz zugewiesen sind,
   * als eindeutige Schlüssel/Label-Paare. Benutzer und Mitarbeiter bekommen
   * getrennte Präfixe, damit sich ihre IDs nicht überschneiden können.
   */
  private getAssignedPersonEntries(m: Maintenance): { key: string; label: string }[] {
    const entries = new Map<string, string>();
    for (const u of m.assignedUsers ?? []) {
      entries.set(`user:${u.id}`, u.userName);
    }
    if (m.assignedUser) {
      entries.set(`user:${m.assignedUser.id}`, m.assignedUser.userName);
    }
    if (m.assignedEmployee) {
      entries.set(`emp:${m.assignedEmployee.id}`, m.assignedEmployee.fullName);
    }
    return Array.from(entries, ([key, label]) => ({ key, label }));
  }

  private rebuildServiceCallPersonOptions(): void {
    const byKey = new Map<string, ServiceCallPersonOption>();
    let unassignedCount = 0;

    for (const m of this.serviceCallMaintenancesCache) {
      const entries = this.getAssignedPersonEntries(m);
      if (entries.length === 0) {
        unassignedCount++;
        continue;
      }
      for (const { key, label } of entries) {
        const existing = byKey.get(key);
        if (existing) {
          existing.count++;
        } else {
          byKey.set(key, { key, label, count: 1 });
        }
      }
    }

    const options = Array.from(byKey.values())
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
    if (unassignedCount > 0) {
      options.push({ key: 'unassigned', label: 'Ohne Zuweisung', count: unassignedCount });
    }
    this.serviceCallPersonOptions = options;

    if (this.serviceCallPersonFilter !== 'all' && !byKey.has(this.serviceCallPersonFilter) &&
        !(this.serviceCallPersonFilter === 'unassigned' && unassignedCount > 0)) {
      this.serviceCallPersonFilter = 'all';
    }
  }

  private applyServiceCallPersonFilter(): void {
    const filter = this.serviceCallPersonFilter;
    if (filter === 'all') {
      this.filteredServiceCallMaintenancesCache = this.serviceCallMaintenancesCache;
      return;
    }
    this.filteredServiceCallMaintenancesCache = this.serviceCallMaintenancesCache.filter(m => {
      const entries = this.getAssignedPersonEntries(m);
      if (filter === 'unassigned') return entries.length === 0;
      return entries.some(e => e.key === filter);
    });
  }

  deleteMaintenance(m: Maintenance, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Auftrag löschen', message: `"${m.title}" wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(r => {
      if (r) {
        this.maintenanceService.deleteMaintenance(m.id).subscribe({
          next: () => { this.reload(); this.snackBar.open('Gelöscht', 'OK', { duration: 2000 }); },
          error: () => this.snackBar.open('Fehler', 'OK', { duration: 4000 })
        });
      }
    });
  }

  // --- Materials ---
  get materials(): CustomerMaterialItem[] {
    return this.customer?.materialItems ?? [];
  }

  newMaterial: Partial<CustomerMaterialItem> = { name: '', quantity: 1, unit: 'Stk', description: '' };
  addingMaterial = false;
  showMaterialForm = false;
  newHourlyRate: Partial<CustomerHourlyRate> = { label: '', hourlyRate: 130, isDefault: false, sortOrder: 0 };
  editingHourlyRateId: number | null = null;

  toggleMaterialForm(): void {
    this.showMaterialForm = !this.showMaterialForm;
    this.newMaterial = { name: '', quantity: 1, unit: 'Stk', description: '' };
  }

  saveMaterial(): void {
    if (!this.customer || !this.newMaterial.name?.trim()) return;
    this.addingMaterial = true;
    this.customerService.addMaterial(this.customer.id, {
      name: this.newMaterial.name!,
      quantity: this.newMaterial.quantity ?? 1,
      unit: this.newMaterial.unit ?? 'Stk',
      description: this.newMaterial.description,
      isPacked: false
    }).subscribe({
      next: () => {
        this.addingMaterial = false;
        this.showMaterialForm = false;
        this.reload();
      },
      error: () => { this.addingMaterial = false; }
    });
  }

  deleteMaterial(mat: CustomerMaterialItem): void {
    if (!this.customer) return;
    this.customerService.deleteMaterial(this.customer.id, mat.id).subscribe({
      next: () => this.reload(),
      error: () => this.snackBar.open('Fehler', 'OK', { duration: 3000 })
    });
  }

  // --- Hourly rates ---
  get hourlyRates(): CustomerHourlyRate[] {
    return this.customer?.hourlyRates ?? [];
  }

  startEditHourlyRate(rate: CustomerHourlyRate): void {
    this.editingHourlyRateId = rate.id;
    this.newHourlyRate = { ...rate };
  }

  cancelHourlyRateEdit(): void {
    this.editingHourlyRateId = null;
    this.newHourlyRate = { label: '', hourlyRate: 130, isDefault: this.hourlyRates.length === 0, sortOrder: this.hourlyRates.length };
  }

  saveHourlyRate(): void {
    if (!this.customer || !this.newHourlyRate.label?.trim()) return;

    const payload = {
      label: this.newHourlyRate.label.trim(),
      hourlyRate: Number(this.newHourlyRate.hourlyRate ?? 0),
      isDefault: !!this.newHourlyRate.isDefault,
      sortOrder: Number(this.newHourlyRate.sortOrder ?? this.hourlyRates.length)
    };

    const request: any = this.editingHourlyRateId
      ? this.customerService.updateHourlyRate(this.customer.id, this.editingHourlyRateId, payload)
      : this.customerService.addHourlyRate(this.customer.id, payload);

    request.subscribe({
      next: () => {
        this.cancelHourlyRateEdit();
        this.reload();
        this.snackBar.open('Stundensatz gespeichert', 'OK', { duration: 2200 });
      },
      error: () => this.snackBar.open('Stundensatz konnte nicht gespeichert werden', 'OK', { duration: 3500 })
    });
  }

  deleteHourlyRate(rate: CustomerHourlyRate): void {
    if (!this.customer) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Stundensatz löschen', message: `"${rate.label}" wirklich löschen? Bestehende Einsätze behalten ihren gespeicherten Satz.` }
    });

    dialogRef.afterClosed().subscribe(r => {
      if (!r || !this.customer) return;
      this.customerService.deleteHourlyRate(this.customer.id, rate.id).subscribe({
        next: () => {
          this.reload();
          this.snackBar.open('Stundensatz gelöscht', 'OK', { duration: 2200 });
        },
        error: () => this.snackBar.open('Stundensatz konnte nicht gelöscht werden', 'OK', { duration: 3500 })
      });
    });
  }

  // --- Logo ---
  // ─────────────────────────────────────────────
  // Takeda-Leistungsverzeichnis
  //
  // Nutzt unverändert die bestehenden Endpunkte unter
  // /api/stammkunde/{customerId}/takeda/lv (TakedaLeistungsverzeichnisController).
  // Es gibt bewusst keinen zweiten API-Pfad und keine zweite Datenhaltung.
  // ─────────────────────────────────────────────

  /**
   * Der über "TakedaFeatures:CustomerId" konfigurierte Kunde – das Flag kommt vom
   * Server, nicht aus dem Kundennamen.
   */
  get isTakedaCustomer(): boolean {
    return this.customer?.isTakeda === true;
  }

  /**
   * LV pflegen darf, wer es auch serverseitig darf: SuperAdmin, Admin oder
   * Bauleiter genau dieses Stammkunden. Entspricht CheckAccessAsync(requireManage).
   */
  get canManageTakedaLv(): boolean {
    if (this.authService.isSuperAdmin() || this.authService.isAdmin()) return true;
    return this.myMemberships.some(
      m => m.customerId === this.customer?.id && m.role === 'Bauleiter');
  }

  get activeTakedaLv(): TakedaLeistungsverzeichnis | null {
    return this.takedaLeistungsverzeichnisse.find(lv => lv.isActive) ?? null;
  }

  get inactiveTakedaLvs(): TakedaLeistungsverzeichnis[] {
    return this.takedaLeistungsverzeichnisse.filter(lv => !lv.isActive);
  }

  get takedaLvTotalPages(): number {
    return Math.max(1, Math.ceil(this.takedaLvPositionen.length / this.takedaLvPageSize));
  }

  get takedaLvPageStart(): number {
    if (this.takedaLvPositionen.length === 0) return 0;
    return (this.takedaLvCurrentPage - 1) * this.takedaLvPageSize + 1;
  }

  get takedaLvPageEnd(): number {
    return Math.min(
      this.takedaLvCurrentPage * this.takedaLvPageSize,
      this.takedaLvPositionen.length
    );
  }

  get visibleTakedaLvPositionen(): TakedaLvPosition[] {
    const start = (this.takedaLvCurrentPage - 1) * this.takedaLvPageSize;
    return this.takedaLvPositionen.slice(start, start + this.takedaLvPageSize);
  }

  previousTakedaLvPage(): void {
    if (this.takedaLvCurrentPage > 1) this.takedaLvCurrentPage--;
  }

  nextTakedaLvPage(): void {
    if (this.takedaLvCurrentPage < this.takedaLvTotalPages) this.takedaLvCurrentPage++;
  }

  private resetTakedaLvState(): void {
    this.takedaLeistungsverzeichnisse = [];
    this.takedaLvPositionen = [];
    this.takedaLvPositionsVisible = false;
    this.takedaLvSearch = '';
    this.takedaLvCurrentPage = 1;
    this.selectedTakedaLvFile = null;
    this.takedaLvAdding = false;
    this.takedaLvSaving = false;
    this.editingTakedaLvPositionId = null;
    this.takedaLvNewPosition = this.createEmptyTakedaLvPosition();
    this.takedaLvEditPosition = null;
  }

  /** Stammkunden-Mitgliedschaften nur für Takeda nachladen (sonst unnötig). */
  private loadTakedaLvBerechtigung(): void {
    this.authService.getMyPermissions().subscribe({
      next: permissions => {
        this.myMemberships = permissions?.stammkundenMemberships ?? [];
      },
      error: () => {
        // Fail-closed: ohne Mitgliedschaften bleibt nur Admin/SuperAdmin übrig.
        this.myMemberships = [];
      }
    });
  }

  loadTakedaLeistungsverzeichnisse(): void {
    if (!this.customer || !this.isTakedaCustomer) return;

    this.takedaLvLoading = true;

    this.stammkundeService.getTakedaLeistungsverzeichnisse(this.customer.id).subscribe({
      next: lvs => {
        this.takedaLeistungsverzeichnisse = lvs ?? [];
        this.takedaLvLoading = false;

        // Wurde das aktive LV gewechselt, alte Positionsliste verwerfen.
        this.takedaLvPositionen = [];
        this.takedaLvPositionsVisible = false;
        this.takedaLvSearch = '';
      },
      error: error => {
        this.takedaLvLoading = false;
        const message = error?.error?.message ?? 'Leistungsverzeichnis konnte nicht geladen werden.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  onTakedaLvFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedTakedaLvFile = input.files?.[0] ?? null;
  }

  importTakedaLv(fileInput: HTMLInputElement): void {
    if (!this.customer || !this.selectedTakedaLvFile || this.takedaLvImporting) return;

    this.takedaLvImporting = true;

    this.stammkundeService
      .importTakedaLeistungsverzeichnis(this.customer.id, this.selectedTakedaLvFile)
      .subscribe({
        next: lv => {
          this.takedaLvImporting = false;
          this.selectedTakedaLvFile = null;
          fileInput.value = '';

          this.snackBar.open(
            `Leistungsverzeichnis importiert (${lv.positionCount} Positionen)`,
            'OK',
            { duration: 4000 }
          );

          this.loadTakedaLeistungsverzeichnisse();
        },
        error: error => {
          this.takedaLvImporting = false;
          const message =
            error?.error?.message ??
            'Leistungsverzeichnis konnte nicht importiert werden.';
          this.snackBar.open(message, 'OK', { duration: 6000 });
        }
      });
  }

  toggleTakedaLvPositionen(): void {
    if (this.takedaLvPositionsVisible) {
      this.takedaLvPositionsVisible = false;
      return;
    }

    this.takedaLvPositionsVisible = true;

    if (this.takedaLvPositionen.length === 0) {
      this.loadTakedaLvPositionen();
    }
  }

  searchTakedaLvPositionen(): void {
    this.takedaLvPositionsVisible = true;
    this.takedaLvCurrentPage = 1;
    this.loadTakedaLvPositionen();
  }

  clearTakedaLvSearch(): void {
    this.takedaLvSearch = '';
    this.takedaLvCurrentPage = 1;
    this.loadTakedaLvPositionen();
  }

  private loadTakedaLvPositionen(): void {
    const lv = this.activeTakedaLv;

    if (!this.customer || !lv) {
      this.takedaLvPositionen = [];
      return;
    }

    this.takedaLvPositionsLoading = true;

    this.stammkundeService
      .getTakedaLvPositionen(this.customer.id, lv.id, this.takedaLvSearch)
      .subscribe({
        next: positionen => {
          this.takedaLvPositionen = positionen ?? [];
          this.takedaLvCurrentPage = Math.min(
            Math.max(1, this.takedaLvCurrentPage),
            this.takedaLvTotalPages
          );
          this.takedaLvPositionsLoading = false;
        },
        error: error => {
          this.takedaLvPositionsLoading = false;
          const message =
            error?.error?.message ??
            'LV-Positionen konnten nicht geladen werden.';
          this.snackBar.open(message, 'OK', { duration: 5000 });
        }
      });
  }

  startAddTakedaLvPosition(): void {
    if (!this.canManageTakedaLv || !this.activeTakedaLv) return;

    this.editingTakedaLvPositionId = null;
    this.takedaLvEditPosition = null;
    this.takedaLvNewPosition = this.createEmptyTakedaLvPosition();
    this.takedaLvAdding = true;
  }

  cancelAddTakedaLvPosition(): void {
    this.takedaLvAdding = false;
    this.takedaLvNewPosition = this.createEmptyTakedaLvPosition();
  }

  saveNewTakedaLvPosition(): void {
    const lv = this.activeTakedaLv;
    if (!this.customer || !lv || this.takedaLvSaving) return;

    if (!this.isTakedaLvPositionValid(this.takedaLvNewPosition)) {
      this.snackBar.open('Bitte mindestens Nummer oder Stichwort ausfüllen.', 'OK', { duration: 3500 });
      return;
    }

    this.takedaLvSaving = true;

    this.stammkundeService
      .addTakedaLvPosition(this.customer.id, lv.id, this.normalizeTakedaLvPosition(this.takedaLvNewPosition))
      .subscribe({
        next: () => {
          this.takedaLvSaving = false;
          this.takedaLvAdding = false;
          this.takedaLvNewPosition = this.createEmptyTakedaLvPosition();
          lv.positionCount += 1;
          this.loadTakedaLvPositionen();
          this.snackBar.open('LV-Zeile hinzugefügt', 'OK', { duration: 3000 });
        },
        error: error => {
          this.takedaLvSaving = false;
          const message = error?.error?.message ?? 'LV-Zeile konnte nicht hinzugefügt werden.';
          this.snackBar.open(message, 'OK', { duration: 5000 });
        }
      });
  }

  startEditTakedaLvPosition(position: TakedaLvPosition): void {
    if (!this.canManageTakedaLv) return;

    this.takedaLvAdding = false;
    this.editingTakedaLvPositionId = position.id;
    this.takedaLvEditPosition = {
      nummer: position.nummer ?? '',
      stichwort: position.stichwort ?? null,
      bezeichnung: position.bezeichnung ?? null,
      typ: position.typ ?? null,
      lvMenge: position.lvMenge ?? null,
      einheit: position.einheit ?? null,
      lohn: position.lohn ?? null,
      sonstiges: position.sonstiges ?? null,
      einheitspreis: position.einheitspreis ?? null,
      gesamtbetrag: position.gesamtbetrag ?? null,
      markierungskennzeichen: position.markierungskennzeichen ?? null,
      lvNotiz: position.lvNotiz ?? null
    };
  }

  cancelEditTakedaLvPosition(): void {
    this.editingTakedaLvPositionId = null;
    this.takedaLvEditPosition = null;
  }

  saveEditTakedaLvPosition(position: TakedaLvPosition): void {
    const lv = this.activeTakedaLv;
    const edit = this.takedaLvEditPosition;

    if (!this.customer || !lv || !edit || this.takedaLvSaving) return;

    if (!this.isTakedaLvPositionValid(edit)) {
      this.snackBar.open('Bitte mindestens Nummer oder Stichwort ausfüllen.', 'OK', { duration: 3500 });
      return;
    }

    this.takedaLvSaving = true;

    this.stammkundeService
      .updateTakedaLvPosition(
        this.customer.id,
        lv.id,
        position.id,
        this.normalizeTakedaLvPosition(edit))
      .subscribe({
        next: updated => {
          this.takedaLvSaving = false;
          const index = this.takedaLvPositionen.findIndex(p => p.id === updated.id);
          if (index >= 0) {
            this.takedaLvPositionen[index] = updated;
          }
          this.cancelEditTakedaLvPosition();
          this.snackBar.open('LV-Zeile aktualisiert', 'OK', { duration: 3000 });
        },
        error: error => {
          this.takedaLvSaving = false;
          const message = error?.error?.message ?? 'LV-Zeile konnte nicht gespeichert werden.';
          this.snackBar.open(message, 'OK', { duration: 5000 });
        }
      });
  }

  deleteTakedaLvPosition(position: TakedaLvPosition): void {
    const lv = this.activeTakedaLv;
    if (!this.customer || !lv || !this.canManageTakedaLv) return;

    const label = [position.nummer, position.bezeichnung ?? position.stichwort]
      .filter(Boolean)
      .join(' – ') || 'diese Zeile';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'LV-Zeile löschen',
        message: `„${label}“ wirklich aus dem Leistungsverzeichnis löschen?`
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed || !this.customer) return;

      this.stammkundeService
        .deleteTakedaLvPosition(this.customer.id, lv.id, position.id)
        .subscribe({
          next: () => {
            this.takedaLvPositionen = this.takedaLvPositionen.filter(p => p.id !== position.id);
            this.takedaLvCurrentPage = Math.min(this.takedaLvCurrentPage, this.takedaLvTotalPages);
            lv.positionCount = Math.max(0, lv.positionCount - 1);
            if (this.editingTakedaLvPositionId === position.id) {
              this.cancelEditTakedaLvPosition();
            }
            this.snackBar.open('LV-Zeile gelöscht', 'OK', { duration: 3000 });
          },
          error: error => {
            const message = error?.error?.message ?? 'LV-Zeile konnte nicht gelöscht werden.';
            this.snackBar.open(message, 'OK', { duration: 5000 });
          }
        });
    });
  }

  private createEmptyTakedaLvPosition(): TakedaLvPositionSave {
    return {
      nummer: '',
      stichwort: null,
      bezeichnung: null,
      typ: 'P',
      lvMenge: null,
      einheit: null,
      lohn: null,
      sonstiges: null,
      einheitspreis: null,
      gesamtbetrag: null,
      markierungskennzeichen: null,
      lvNotiz: null
    };
  }

  private isTakedaLvPositionValid(position: TakedaLvPositionSave): boolean {
    return !!position.nummer?.trim() || !!position.stichwort?.trim();
  }

  private normalizeTakedaLvPosition(position: TakedaLvPositionSave): TakedaLvPositionSave {
    const text = (value?: string | null): string | null => {
      const result = value?.trim();
      return result ? result : null;
    };

    return {
      ...position,
      nummer: position.nummer?.trim() ?? '',
      stichwort: text(position.stichwort),
      bezeichnung: text(position.bezeichnung),
      typ: text(position.typ),
      einheit: text(position.einheit),
      markierungskennzeichen: text(position.markierungskennzeichen),
      lvNotiz: text(position.lvNotiz)
    };
  }

  takedaLvTypLabel(typ?: string | null): string {
    switch ((typ ?? '').trim().toUpperCase()) {
      case 'P': return 'Position';
      case 'LG': return 'Leistungsgruppe';
      case 'UG': return 'Untergruppe';
      case 'GT': return 'Gruppentext';
      default: return typ || '–';
    }
  }

  trackByTakedaLvPositionId(_: number, position: TakedaLvPosition): number {
    return position.id;
  }

  // ─────────────────────────────────────────────
  // Stundensatz-Zuordnung (nur Takeda)
  //
  // Gespeichert wird ausschließlich die Verbindung Person -> CustomerHourlyRate.
  // Der Preis selbst bleibt im Stundensatz darüber – es gibt bewusst keine
  // zweite Preisquelle an der Person.
  // ─────────────────────────────────────────────

  /**
   * Ändern darf, wer es auch serverseitig darf: SuperAdmin, Admin oder Bauleiter
   * dieses Stammkunden – dazu die kaufmännischen Rollen, die die Stundensätze
   * ohnehin pflegen. Der Vorarbeiter ist in keiner dieser Gruppen.
   */
  get canManageHourlyRateAssignments(): boolean {
    // Dieselbe Stufe wie das Anlegen der Sätze: wer einen Satz zuordnen darf,
    // muss ihn auch anlegen können – und umgekehrt.
    return this.canManageHourlyRates;
  }

  /**
   * Regiestundensätze anlegen/ändern/löschen. Spiegelt CanManageHourlyRates im
   * CustomerController: grundsätzlich das Preisrecht, bei Takeda zusätzlich der
   * Bauleiter dieses Stammkunden. Der Server bleibt maßgeblich.
   */
  get canManageHourlyRates(): boolean {
    // CanEditPrices serverseitig: SuperAdmin, Admin, Kaufmännisch, ProjektleiterKaufmann.
    const canEditPrices = this.authService.isSuperAdmin()
      || this.authService.isAdmin()
      || this.authService.isCommercial()
      || this.authService.isProjektleiterKaufmann();

    if (canEditPrices) { return true; }

    return this.isTakedaCustomer && this.canManageTakedaLv;
  }

  /** Eindeutiger Schlüssel einer Zeile – Benutzer und Arbeiter teilen sich keinen Id-Raum. */
  hourlyRatePersonKey(person: CustomerHourlyRatePerson): string {
    return buildHourlyRatePersonKey(person);
  }

  /**
   * Angular ruft trackBy ohne Komponentenkontext auf – hier darf deshalb kein
   * `this` verwendet werden. Der Schlüssel kommt aus der freien Funktion.
   */
  trackByHourlyRatePerson(_: number, person: CustomerHourlyRatePerson): string {
    return buildHourlyRatePersonKey(person);
  }

  private loadHourlyRateAssignments(): void {
    if (!this.customer || !this.isTakedaCustomer) return;

    this.hourlyRatePersonsLoading = true;

    this.customerService.getHourlyRateAssignments(this.customer.id).subscribe({
      next: persons => {
        this.hourlyRatePersons = persons ?? [];
        this.hourlyRatePersonsLoading = false;
      },
      error: error => {
        this.hourlyRatePersons = [];
        this.hourlyRatePersonsLoading = false;
        const message = error?.error?.message
          ?? 'Die Stundensatz-Zuordnungen konnten nicht geladen werden.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  /**
   * Direktes Speichern beim Wechsel im Select – ein zusätzlicher Speichern-Button
   * wäre bei einer einzelnen Auswahl je Zeile nur ein Klick mehr.
   * `null` bedeutet "Nicht zugeordnet" und entfernt die Zuordnung.
   * Schlägt der Aufruf fehl, wird der vorherige Wert wiederhergestellt.
   */
  onHourlyRateAssignmentChange(
    person: CustomerHourlyRatePerson,
    rateId: number | null
  ): void {
    if (!this.customer || !this.canManageHourlyRateAssignments) return;

    const key = this.hourlyRatePersonKey(person);
    if (this.savingAssignmentKey === key) return;

    const vorher = person.customerHourlyRateId ?? null;
    const vorherLabel = person.hourlyRateLabel ?? null;

    if ((rateId ?? null) === vorher) return;

    this.savingAssignmentKey = key;
    person.customerHourlyRateId = rateId;

    this.customerService.setHourlyRateAssignment(this.customer.id, {
      customerHourlyRateId: rateId,
      userId: person.userId ?? null,
      workerId: person.workerId ?? null
    }).subscribe({
      next: updated => {
        this.savingAssignmentKey = null;
        person.customerHourlyRateId = updated.customerHourlyRateId ?? null;
        person.hourlyRateLabel = updated.hourlyRateLabel ?? null;

        this.snackBar.open(
          updated.customerHourlyRateId
            ? `${person.personName}: ${updated.hourlyRateLabel}`
            : `${person.personName}: Zuordnung entfernt`,
          'OK',
          { duration: 2500 });
      },
      error: error => {
        this.savingAssignmentKey = null;
        // Alten Stand wiederherstellen, damit die Anzeige nicht lügt.
        person.customerHourlyRateId = vorher;
        person.hourlyRateLabel = vorherLabel;

        const message = error?.error?.message
          ?? 'Die Zuordnung konnte nicht gespeichert werden.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  getLogoUrl(logoPath: string): string {
    return this.customerService.getLogoUrl(logoPath);
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.customer) return;
    const file = input.files[0];
    this.customerService.uploadLogo(this.customer.id, file).subscribe({
      next: () => {
        this.reload();
        this.snackBar.open('Logo hochgeladen', 'OK', { duration: 3000 });
      },
      error: () => this.snackBar.open('Fehler beim Hochladen', 'OK', { duration: 4000 })
    });
    input.value = '';
  }

  removeLogo(): void {
    if (!this.customer) return;
    this.customerService.deleteLogo(this.customer.id).subscribe({
      next: () => {
        this.reload();
        this.snackBar.open('Logo entfernt', 'OK', { duration: 3000 });
      },
      error: () => this.snackBar.open('Fehler', 'OK', { duration: 4000 })
    });
  }

  // --- Helpers ---
  getStatusClass(status: MaintenanceStatus): string {
    switch (status) {
      case MaintenanceStatus.Planned: return 'status-planned';
      case MaintenanceStatus.InProgress: return 'status-progress';
      case MaintenanceStatus.Completed: return 'status-completed';
      case MaintenanceStatus.Overdue: return 'status-overdue';
      case MaintenanceStatus.Cancelled: return 'status-cancelled';
      default: return '';
    }
  }

  getStatusLabel(status: MaintenanceStatus): string {
    switch (status) {
      case MaintenanceStatus.Planned: return 'Geplant';
      case MaintenanceStatus.InProgress: return 'In Arbeit';
      case MaintenanceStatus.Completed: return 'Erledigt';
      case MaintenanceStatus.Overdue: return 'Überfällig';
      case MaintenanceStatus.Cancelled: return 'Storniert';
      default: return status;
    }
  }

  getRecurrenceLabel(type: MaintenanceRecurrenceType): string {
    switch (type) {
      case MaintenanceRecurrenceType.Daily: return 'Täglich';
      case MaintenanceRecurrenceType.Weekly: return 'Wöchentlich';
      case MaintenanceRecurrenceType.Monthly: return 'Monatlich';
      case MaintenanceRecurrenceType.Quarterly: return 'Vierteljährlich';
      case MaintenanceRecurrenceType.Yearly: return 'Jährlich';
      default: return 'Einmalig';
    }
  }

  getCompletionPercent(): number {
    return this.completionPercentCache;
  }

  getCustomerInitial(customer: Customer): string {
    return customer.name?.trim().charAt(0).toUpperCase() || '?';
  }

  hasContactDetails(customer: Customer): boolean {
    return !!(
      customer.contactPersonName?.trim() ||
      customer.contactPersonPhone?.trim() ||
      customer.contactPersonEmail?.trim()
    );
  }

  getOpenMaintenanceCount(): number {
    return this.openMaintenanceCountCache;
  }

  getOverdueMaintenanceCount(): number {
    return this.overdueMaintenanceCountCache;
  }

  trackByEquipmentId(_: number, equipment: Equipment): number {
    return equipment.id;
  }

  trackByMaintenanceId(_: number, maintenance: Maintenance): number {
    return maintenance.id;
  }

  trackByCustomerMaterialId(_: number, material: CustomerMaterialItem): number {
    return material.id;
  }

  trackByMaintenanceMaterialId(_: number, material: { id?: number }): number | undefined {
    return material.id;
  }

  trackByHourlyRateId(_: number, rate: CustomerHourlyRate): number {
    return rate.id;
  }

  getTelLink(phone: string): string {
    const normalized = phone.replace(/[^\d+]/g, '');
    return `tel:${normalized}`;
  }

  getMailtoLink(email: string): string {
    return `mailto:${email.trim()}`;
  }

  getMapLink(address: string): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }

  getTechnicianNames(m: Maintenance): string {
    if (!m.assignedUsers?.length) return '–';
    return m.assignedUsers.map(u => u.userName).join(', ');
  }
}
